import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useTranslation } from 'react-i18next';

const VideoCarousel = ({ videoSources = [] }) => {
  const { t } = useTranslation();
  // Extract just the src from videoSources for backward compatibility
  const videos = videoSources.map(v => v.src);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRefs = useRef([]);
  const containerRef = useRef(null);
  const userInteracted = useRef(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Calculate responsive dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const height = isMobile ? width * 0.6 : width * 0.5625; // 16:9 aspect ratio
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isMobile]);

  // Set mounted state after component mounts
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Handle video end to play next video
  const handleVideoEnd = () => {
    if (!isMounted) return;
    setCurrentVideoIndex((prevIndex) => 
      prevIndex === videos.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Play/pause toggle
  const togglePlayPause = () => {
    const video = videoRefs.current[currentVideoIndex];
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play().catch(error => {
          console.error("Play failed:", error);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    const video = videoRefs.current[currentVideoIndex];
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  // Handle user interaction
  const handleUserInteraction = () => {
    if (!userInteracted.current) {
      userInteracted.current = true;
      if (!isPlaying) {
        togglePlayPause();
      }
    }
  };

  // Navigate to next video
  const nextVideo = () => {
    if (!isMounted) return;
    setCurrentVideoIndex((prevIndex) => 
      prevIndex === videos.length - 1 ? 0 : prevIndex + 1
    );
    setIsPlaying(true);
  };

  // Navigate to previous video
  const prevVideo = () => {
    if (!isMounted) return;
    setCurrentVideoIndex((prevIndex => 
      prevIndex === 0 ? videos.length - 1 : prevIndex - 1
    ));
    setIsPlaying(true);
  };

  // Handle video load
  const handleVideoLoaded = (index) => {
    if (index === currentVideoIndex && isPlaying) {
      const video = videoRefs.current[index];
      if (video) {
        video.muted = isMuted;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error("Auto-play was prevented:", error);
            setIsPlaying(false);
          });
        }
      }
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) { /* Safari */
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) { /* IE11 */
        containerRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) { /* Safari */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) { /* IE11 */
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  if (videos.length === 0) {
    return <Typography>{t('no_videos_available', { defaultValue: 'No videos available' })}</Typography>;
  }

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        width: '100%', 
        maxWidth: '1000px',
        margin: '0 auto',
        position: 'relative',
        padding: { xs: 1, sm: 2 },
        boxSizing: 'border-box',
        bgcolor: isFullscreen ? '#000' : 'transparent',
        height: isFullscreen ? '100vh' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '&:fullscreen': {
          padding: 0,
          '& .video-container': {
            height: '100%',
            '& video': {
              objectFit: 'contain',
              maxHeight: '100vh',
            }
          },
          '& .video-controls': {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
            padding: '20px',
          }
        }
      }}
      onClick={handleUserInteraction}
    >
      <Box 
        className="video-container"
        sx={{
          position: 'relative',
          width: '100%',
          height: isFullscreen ? 'calc(100vh - 80px)' : (dimensions.height || 'auto'),
          minHeight: isMobile ? '200px' : '400px',
          margin: '0 auto',
          borderRadius: isFullscreen ? 0 : '8px',
          overflow: 'hidden',
          boxShadow: isFullscreen ? 'none' : 3,
          backgroundColor: '#000',
          '&:hover .video-controls': {
            opacity: 1,
          }
        }}
      >
        <video
          ref={el => videoRefs.current[currentVideoIndex] = el}
          key={currentVideoIndex}
          width="100%"
          height="100%"
          onEnded={handleVideoEnd}
          onLoadedData={() => handleVideoLoaded(currentVideoIndex)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          style={{ 
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            backgroundColor: '#000'
          }}
          poster={currentVideoIndex === 0 && videoSources[0]?.thumbnailTime !== undefined 
            ? `${videos[0]}#t=${videoSources[0].thumbnailTime}` 
            : ''}
          controls={false}
          playsInline
          muted={isMuted}
        >
          <source 
            src={`${videos[currentVideoIndex]}${
              currentVideoIndex === 0 && videoSources[0]?.thumbnailTime !== undefined 
                ? `#t=${videoSources[0].thumbnailTime}` 
                : ''
            }`}
            type="video/mp4" 
            onLoadedData={(e) => {
              // Reset to beginning after thumbnail is shown
              if (currentVideoIndex === 0 && videoSources[0]?.thumbnailTime !== undefined) {
                e.target.currentTime = 0;
              }
            }}
          />
          {t('video_not_supported', { defaultValue: 'Your browser does not support the video tag.' })}
        </video>
        
        {/* Video Controls */}
        <Box
          className="video-controls"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
            padding: { xs: 1, sm: 2 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            zIndex: 2,
            opacity: 0.8,
            transition: 'opacity 0.3s ease',
            '&:hover': {
              opacity: 1,
            }
          }}
        >
          <IconButton onClick={prevVideo} sx={{ color: 'white' }}>
            <NavigateBeforeIcon />
          </IconButton>
          
          <IconButton onClick={togglePlayPause} sx={{ color: 'white' }}>
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          
          <IconButton onClick={toggleMute} sx={{ color: 'white' }}>
            {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
          </IconButton>
          
          <IconButton onClick={nextVideo} sx={{ color: 'white' }}>
            <NavigateNextIcon />
          </IconButton>
          
          <Typography variant="body2" sx={{ 
            color: 'white', 
            ml: 1,
            minWidth: '60px',
            textAlign: 'center',
            fontSize: { xs: '0.8rem', sm: '0.9rem' }
          }}>
            {currentVideoIndex + 1} / {videos.length}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={toggleFullscreen} 
            sx={{ 
              color: 'white',
              ml: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
      </Box>
      
      {/* Video Thumbnails */}
      <Box sx={{ 
        display: 'flex', 
        overflowX: 'auto',
        gap: 1,
        padding: { xs: 1, sm: 2 },
        margin: '0 auto',
        maxWidth: '1000px',
        '&::-webkit-scrollbar': {
          height: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
        },
      }}>
        {videos.map((src, index) => (
          <Box
            key={index}
            onClick={() => {
              setCurrentVideoIndex(index);
              setIsPlaying(true);
              userInteracted.current = true;
            }}
            sx={{
              flex: '0 0 auto',
              width: { xs: '80px', sm: '120px' },
              height: { xs: '50px', sm: '70px' },
              borderRadius: '4px',
              overflow: 'hidden',
              cursor: 'pointer',
              position: 'relative',
              border: currentVideoIndex === index ? '3px solid #f1501a' : 'none',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <video
              src={`${src}#t=${videoSources[index]?.thumbnailTime || 0}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              muted
              preload="metadata"
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0,0,0,0.3)',
              }}
            >
              <PlayArrowIcon sx={{ color: 'white', fontSize: { xs: '1.5rem', sm: '2rem' } }} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default VideoCarousel;
